"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaClock } from "react-icons/fa";
import HeroSection from "../../components/common/HeroSection";

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

interface Toast {
  type: 'success' | 'error';
  message: string;
}

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<Toast | null>(null);

  // Add useEffect for auto-dismiss
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 3000); // Toast will disappear after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [toast]);

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        if (value.trim().length > 50) return 'Name must be less than 50 characters';
        if (!/^[a-zA-Z\s]*$/.test(value)) return 'Name can only contain letters and spaces';
        return '';

      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
        return '';

      case 'subject':
        if (!value.trim()) return 'Subject is required';
        if (value.trim().length < 5) return 'Subject must be at least 5 characters';
        if (value.trim().length > 100) return 'Subject must be less than 100 characters';
        return '';

      case 'message':
        if (!value.trim()) return 'Message is required';
        if (value.trim().length < 20) return 'Message must be at least 20 characters';
        if (value.trim().length > 1000) return 'Message must be less than 1000 characters';
        return '';

      default:
        return '';
    }
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field as keyof typeof formData]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    const newErrors: FormErrors = {};
    let isValid = true;

    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key as keyof typeof formData]);
      if (error) {
        newErrors[key as keyof FormErrors] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));

    if (!isValid) {
      setToast({
        type: 'error',
        message: 'Please fill all required fields.'
      });
      return;
    }

    // Handle form submission logic here
    console.log(formData);
    
    // Show success toast
    setToast({
      type: 'success',
      message: 'Thanks! We’ll be in touch.'
    });

    // Reset form
    setFormData({
      name: "",
      email: "",
      subject: "",
      message: ""
    });
    setTouched({});
  };

  const renderInput = (
    name: keyof typeof formData,
    label: string,
    type: string = 'text',
    placeholder: string
  ) => (
    <div>
      <label className="block text-gray-700 mb-2">{label}</label>
      <input
        type={type}
        name={name}
        required
        className={`w-full px-4 py-3 rounded-xl border ${
          errors[name] && touched[name] ? 'border-red-500' : 'border-gray-200'
        } focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/20 outline-none transition-all duration-200`}
        placeholder={placeholder}
        value={formData[name]}
        onChange={handleChange}
        onBlur={() => handleBlur(name)}
      />
    </div>
  );

  return (
    <main className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-white mb-8">
      {/* Toast Notification */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 20 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed bottom-8 right-4 z-50"
          >
            <div
              className={`px-6 py-3 rounded-xl shadow-lg flex items-center space-x-2 ${
                toast.type === 'success' 
                  ? 'bg-green-50 text-green-700 border border-green-200' 
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}
            >
              {toast.type === 'success' ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              <span className="font-medium">{toast.message}</span>
              <motion.div
                className="w-1 h-1 bg-current rounded-full"
                initial={{ scale: 1 }}
                animate={{ scale: 0 }}
                transition={{ duration: 3, ease: "linear" }}
              />
              <button
                onClick={() => setToast(null)}
                className="ml-2 text-gray-500 hover:text-gray-700"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <section>
        <HeroSection 
          title="Let's Connect"
          description="Have questions or want to discuss your project? We're here to help!"
          showPattern={true}
        />  
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-3xl shadow-lg p-8"
              >
                <h2 className="text-2xl font-bold mb-6">Contact Details</h2>
                <div className="space-y-6">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="flex items-center space-x-4 p-4 rounded-2xl hover:bg-gray-50"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 
                      text-white flex items-center justify-center">
                      <FaEnvelope className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">Email</h3>
                      <p className="text-gray-600">info@asciibytes.com</p>
                    </div>
                  </motion.div>

                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="flex items-center space-x-4 p-4 rounded-2xl hover:bg-gray-50"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 
                      text-white flex items-center justify-center">
                      <FaPhone className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">Phone</h3>
                      <p className="text-gray-600">+91 8888216196</p>
                    </div>
                  </motion.div>

                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="flex items-center space-x-4 p-4 rounded-2xl hover:bg-gray-50"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 
                      text-white flex items-center justify-center">
                      <FaClock className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">Business Hours</h3>
                      <p className="text-gray-600">Mon - Fri: 9AM - 6PM</p>
                    </div>
                  </motion.div>

                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="flex items-center space-x-4 p-4 rounded-2xl hover:bg-gray-50"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 
                      text-white flex items-center justify-center">
                      <FaMapMarkerAlt className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">Location</h3>
                      <p className="text-gray-600">Ichalkaranji, Maharashtra</p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-3xl shadow-lg p-8"
              >
                <h2 className="text-2xl font-bold mb-6">Send us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {renderInput('name', 'Name', 'text', 'Your name')}
                    {renderInput('email', 'Email', 'email', 'Your email')}
                  </div>

                  {renderInput('subject', 'Subject', 'text', "What's this about?")}

                  <div>
                    <label className="block text-gray-700 mb-2">Message</label>
                    <textarea
                      name="message"
                      required
                      rows={6}
                      className={`w-full px-4 py-3 rounded-xl border ${
                        errors.message && touched.message ? 'border-red-500' : 'border-gray-200'
                      } focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/20 outline-none transition-all duration-200 resize-none`}
                      placeholder="Tell us about your project..."
                      value={formData.message}
                      onChange={handleChange}
                      onBlur={() => handleBlur('message')}
                    />
                    {errors.message && touched.message && (
                      <p className="mt-2 text-sm text-red-600">{errors.message}</p>
                    )}
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="w-full px-8 py-4 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 
                      text-white font-semibold hover:shadow-lg transition-all duration-200"
                  >
                    Send Message
                  </motion.button>
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}